// src/components/user/express/useExpressDelivery.js
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || '';

export function useExpressDelivery(userId) {
  // ── Step control ──────────────────────────────────────────
  const [currentStep, setCurrentStep] = useState(1);

  // ── Pricing config (fetched on mount) ────────────────────
  const [pricingConfig, setPricingConfig] = useState({
    bike: { rate_per_km: 1.80, minimum_fare: 15.00, minimum_km: 8.0 },
    car:  { rate_per_km: 4.50, minimum_fare: 15.00, minimum_km: 5.0 },
  });

  // ── Saved addresses ───────────────────────────────────────
  const [savedAddresses, setSavedAddresses] = useState([]);

  // ── Step 1 — Pickup ───────────────────────────────────────
  const [pickupAddress, setPickupAddress] = useState('');
  const [pickupLat, setPickupLat]         = useState(null);
  const [pickupLng, setPickupLng]         = useState(null);

  // ── Step 2 — Dropoff ──────────────────────────────────────
  const [dropoffAddress, setDropoffAddress] = useState('');
  const [dropoffLat, setDropoffLat]         = useState(null);
  const [dropoffLng, setDropoffLng]         = useState(null);
  const [recipientName, setRecipientName]   = useState('');
  const [recipientPhone, setRecipientPhone] = useState('');

  // ── Step 3 — Vehicle + Route ──────────────────────────────
  const [vehicleType, setVehicleType]         = useState(null); // 'bike' | 'car'
  const [routeResult, setRouteResult]         = useState(null); // { distance_km, total_price, polyline, ... }
  const [calculatingRoute, setCalculatingRoute] = useState(false);
  const [routeError, setRouteError]           = useState(null);

  // ── Step 4 — Products ─────────────────────────────────────
  const [items, setItems]       = useState([]); // [{ sku, title, quantity, inventory_id, location_name }]
  const [locations, setLocations] = useState([]); // user's inventory locations
  const [charge, setCharge]       = useState('');
  // ── Submission ────────────────────────────────────────────
  const [submitting, setSubmitting]   = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // ── Fetch pricing config ──────────────────────────────────
  useEffect(() => {
    axios.get(`${BASE_URL}/api/express/pricing-config`)
      .then(res => {
        const map = {};
        res.data.forEach(c => { map[c.vehicle_type] = c; });
        if (map.bike && map.car) setPricingConfig(map);
      })
      .catch(() => {}); // silently fall back to defaults
  }, []);

  // ── Fetch saved addresses ─────────────────────────────────
  useEffect(() => {
    if (!userId) return;
    const token = localStorage.getItem('token');
    axios.get(`${BASE_URL}/api/user_addresses/${userId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(res => setSavedAddresses(res.data.addresses || []))
      .catch(() => setSavedAddresses([]));
  }, [userId]);

  // ── Fetch inventory locations ─────────────────────────────
  useEffect(() => {
    if (!userId) return;
    axios.get(`${BASE_URL}/api/locations`, { params: { user_id: userId } })
      .then(res => setLocations(res.data.locations || res.data || []))
      .catch(() => setLocations([]));
  }, [userId]);

  // ── Calculate route (called from Step 3) ─────────────────
  const calculateRoute = useCallback(async () => {
    if (!pickupLat || !pickupLng || !dropoffLat || !dropoffLng || !vehicleType) return;
    setCalculatingRoute(true);
    setRouteError(null);
    try {
      const res = await axios.post(`${BASE_URL}/api/express/calculate-route`, {
        origin_lat:      pickupLat,
        origin_lng:      pickupLng,
        destination_lat: dropoffLat,
        destination_lng: dropoffLng,
        vehicle_type:    vehicleType,
      });
      setRouteResult(res.data);
    } catch (err) {
      setRouteError(err.response?.data?.error || 'Error al calcular la ruta');
    } finally {
      setCalculatingRoute(false);
    }
  }, [pickupLat, pickupLng, dropoffLat, dropoffLng, vehicleType]);

  // ── Submit delivery ───────────────────────────────────────
  const submitDelivery = useCallback(async () => {
    if (!routeResult) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await axios.post(`${BASE_URL}/api/express/deliveries`, {
        user_id:         userId,
        expected_inventory_owner_user_id: items[0]?.inventory_owner_user_id,
        vehicle_type:    vehicleType,
        pickup_address:  pickupAddress,
        pickup_lat:      pickupLat,
        pickup_lng:      pickupLng,
        dropoff_address: dropoffAddress,
        dropoff_lat:     dropoffLat,
        dropoff_lng:     dropoffLng,
        recipient_name:  recipientName,
        recipient_phone: recipientPhone,
        notes:           '',
        items: items.map(i => ({
          sku:          i.sku,
          quantity:     i.quantity,
          inventory_id: i.inventory_id || null,
        })),
        charge: charge ? parseFloat(charge) : null,
      });
      setSubmitSuccess(true);
    } catch (err) {
      setSubmitError(err.response?.data?.error || 'Error al crear el delivery express');
    } finally {
      setSubmitting(false);
    }
  }, [userId, vehicleType, pickupAddress, pickupLat, pickupLng,
      dropoffAddress, dropoffLat, dropoffLng, recipientName,
      recipientPhone, items, routeResult, charge]);

  // ── Step validation ───────────────────────────────────────
  const canProceedStep1 = !!(pickupAddress && pickupLat && pickupLng);
  const canProceedStep2 = !!(dropoffAddress && dropoffLat && dropoffLng && recipientName.trim() && recipientPhone.trim());
  const canProceedStep3 = !!(vehicleType && routeResult);
  const canProceedStep4 = items.length > 0 && items.every(i => i.sku);

  return {
    // step
    currentStep, setCurrentStep,
    // pricing
    pricingConfig,
    // addresses
    savedAddresses,
    // step 1
    pickupAddress, setPickupAddress,
    pickupLat, setPickupLat,
    pickupLng, setPickupLng,
    // step 2
    dropoffAddress, setDropoffAddress,
    dropoffLat, setDropoffLat,
    dropoffLng, setDropoffLng,
    recipientName, setRecipientName,
    recipientPhone, setRecipientPhone,
    // step 3
    vehicleType, setVehicleType,
    routeResult,
    calculatingRoute,
    routeError,
    calculateRoute,
    // step 4
    items, setItems,
    locations,
    charge, setCharge,
    // submit
    submitting, submitError, submitSuccess,
    submitDelivery,
    // validation
    canProceedStep1, canProceedStep2, canProceedStep3, canProceedStep4,
  };
}
